/**
 * Per-route SEO metadata — one entry per indexable route.
 * Fixes the audit finding: every page previously inherited the homepage
 * canonical + homepage title/description. Each entry here now has a
 * unique title, description, and self-referential canonical.
 *
 * Append to this file as new indexable pages are added.
 */

export const PAGE_SEO = {
  "/": {
    title: "WebsiteTech.pro — AI Websites, SEO & Growth Operator",
    description:
      "Build, modernise and grow your website with AI. Transparent SEO audits, verified rankings and a managed Growth Operator that ships marketing tasks with one-tap client approvals.",
  },
  "/lead-generation": {
    title: "AI Lead Generation System for Service Businesses | WebsiteTech.pro",
    description:
      "Attract, convert, automate, measure and grow — one connected lifecycle. AI-powered lead generation for service businesses with transparent attribution and human approvals.",
  },
  "/ai-revenue-center": {
    title: "AI Revenue Center™ — Coordinated AI Agents with Shared Memory | WebsiteTech.pro",
    description:
      "Scout prospects, Ava answers, Relay follows up. One shared customer timeline across every channel, with Revenue Intelligence and Revival built in.",
  },
  "/growth-operator": {
    title: "Managed AI Growth Operator — Human-Supervised Marketing | WebsiteTech.pro",
    description:
      "A managed service that runs weekly SEO, content and ad tasks and gets one-tap client approval before shipping. Transparent reporting, measurable outcomes, no autopilot risks.",
  },
  "/packages": {
    title: "Pricing & Packages — Starter, Growth and Managed | WebsiteTech.pro",
    description:
      "Transparent starting prices for website design, lead-generation and managed growth. Choose a one-time project or a managed plan, with clear inclusions and next-step timelines.",
  },
  "/tools": {
    title: "Tools Catalog — Every WebsiteTech Capability in One Place",
    description:
      "Browse every WebsiteTech tool — website analyzer, SEO intelligence, AI assistants, growth tasks, local visibility and more. Filter by goal, business type and price model.",
  },
  "/solutions/restaurants": {
    title: "Restaurant Websites, Local SEO & AI Reservations | WebsiteTech.pro",
    description:
      "Restaurant-specific websites with menu management, booking automation, local SEO and review workflows. Built by WebsiteTech for independent restaurants and small groups.",
  },
  "/solutions/healthcare": {
    title: "Healthcare & Clinic Websites with AI Appointment Flows | WebsiteTech.pro",
    description:
      "HIPAA-aware websites for clinics and medical practices — intake forms, appointment booking, review management and AI patient receptionist. Human review on every public claim.",
  },
  "/solutions/travel": {
    title: "Hotel & Travel Websites with AI Concierge | WebsiteTech.pro",
    description:
      "AI-ready websites for hotels, resorts and travel operators — booking integrations, multi-language, review workflows and concierge assistants. Managed by WebsiteTech.",
  },
  "/solutions/professional": {
    title: "Professional Services Websites with Lead Workflows | WebsiteTech.pro",
    description:
      "Websites for law firms, accountants, consultants and agencies — qualified lead capture, booking, document portals and compliant AI assistants.",
  },
  "/solutions/ai-commerce-intelligence": {
    title: "AI Commerce Intelligence — Price, Demand & Publishing | WebsiteTech.pro",
    description:
      "Multi-store price intelligence, demand forecasting, bundle recommendations and approved publishing for independent ecommerce. One connected commerce stack.",
  },
  "/company/about": {
    title: "About WebsiteTech — Our Approach to AI, SEO & Human Approval",
    description:
      "WebsiteTech builds AI-ready websites and runs managed growth tasks under human approval. Learn our principles, methodology, delivery model and privacy stance.",
  },
  "/about": {
    title: "About WebsiteTech — AI, SEO and Human Approval for Growth",
    description:
      "The WebsiteTech story — AI-native websites, verified SEO and a managed Growth Operator that preserves human control over every public change.",
  },
  "/case-studies": {
    title: "Case Studies — Live Client Work, Pilots and Transparent Examples | WebsiteTech.pro",
    description:
      "Every case study is labelled Live client · Pilot · Illustrative so you can distinguish verified outcomes from in-progress work and sample workflows. No fabricated metrics.",
  },
  "/clients": {
    title: "Portfolio — WebsiteTech Clients, Platforms & Projects",
    description:
      "A portfolio of websites, platforms and growth engagements WebsiteTech has delivered — grouped by live clients, pilots and partner platforms.",
  },
  "/si": {
    title: "Search Intelligence — Transparent 4-Score SEO Audit | WebsiteTech.pro",
    description:
      "A free, deterministic SEO audit with four published scores and complete evidence per rule. No opaque Google score, no AI hallucinations — the measurement is the product.",
  },
  "/si/methodology": {
    title: "Search Intelligence Methodology — Evidence, Rules & Scoring",
    description:
      "How WebsiteTech measures and scores SEO. Every rule, signal and weight is published so clients can audit the audit itself.",
  },
  "/si/sample": {
    title: "Search Intelligence — Sample Report",
    description:
      "A sample Search Intelligence audit with the four scores, measured evidence and the recommended fix list — exactly what WebsiteTech delivers to clients.",
  },
  "/features/gallery": {
    title: "Feature Gallery — Every WebsiteTech Capability on One Page",
    description:
      "Visual walkthrough of every WebsiteTech capability — AI assistants, SEO, analytics, lead workflows, growth operator and content tools.",
  },
  "/pricing": {
    title: "Pricing — Transparent Starting Prices and Managed Plans",
    description:
      "Honest, visible pricing for projects and managed services. Compare starters, growth packs and managed plans with clear inclusions.",
  },
  "/visual-studio": {
    title: "Visual Studio — URL to Campaign in Minutes | WebsiteTech.pro",
    description:
      "Enter a URL, generate three visual campaign concepts, approve and export. Rights-aware, draft-watermarked and designed for human approval before anything ships.",
  },
  "/seo-analyzer": {
    title: "SEO Analyzer — Deterministic Audit of Any Page | WebsiteTech.pro",
    description:
      "Scan any URL for 20+ measured SEO checks — canonical, title, meta description, headings, viewport, image alt, structured data, link graph and more. Evidence-first, AI-explained.",
  },
};

export function getPageSeo(pathname) {
  if (!pathname) return null;
  if (PAGE_SEO[pathname]) return PAGE_SEO[pathname];
  // Trim trailing slash
  const trimmed = pathname.replace(/\/+$/, "");
  if (trimmed && PAGE_SEO[trimmed]) return PAGE_SEO[trimmed];
  return null;
}

export default PAGE_SEO;
